import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaDownload, FaExpandArrowsAlt, FaSearch, FaRedo } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Activepolicies.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const CERT_WEBHOOK = 'https://sms.definiteassurance.com/webhook/c924c7c4-d115-4724-a78c-27048b99b6f0';
const PAYMENT_WEBHOOK = 'https://workflows.uuidesign.xyz/webhook/0623c8f7-d286-45c4-b823-2675d25de35a';
const WALLET_WEBHOOK = 'https://sms.definiteassurance.com/webhook/04a58cac-a6b4-4bde-883a-b364f32d9ae9';
const DOUBLE_INS_WEBHOOK = 'https://sms.definiteassurance.com/webhook/d6c9608b-cd90-481a-995d-d40c908c96db';
const COMPANY = 'Definite';
const STAMP_DUTY = 0;
const ROWS_PER_PAGE = 20;
const PSV_PRODUCTS = [4638, 3587, 3586];

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

const formatISODate = (input) => {
  try {
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const formatDateFromMs = (ms) => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (ms) => {
  const d = new Date(ms);
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const getNow = () => formatDateTime(Date.now());

const getDaysInCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
};

const getMinDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
};

const formatDate2 = (inputDate) => {
  try {
    const d = new Date(inputDate);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${month}/${day}/${d.getFullYear()} 11:59PM`;
  } catch { return '--'; }
};

const dateToMs = (input) => {
  if (typeof input === 'string') return new Date(input).getTime();
  if (input instanceof Date) return input.getTime();
  return 0;
};

const getCoverOption = (coption) => {
  if (coption === 1 || coption === 58) return { code: 100, name: 'COMP' };
  if (coption === 2) return { code: 200, name: 'TPO' };
  return { code: '', name: '' };
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Activepolicies() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agentName, setAgentName] = useState('');
  const [dealerCode, setDealerCode] = useState('');
  const [dsfApproved, setDsfApproved] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [iraNumber, setIraNumber] = useState('');
  const [walletNo, setWalletNo] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [availableCommission, setAvailableCommission] = useState(0);

  // ── Policies table ──
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Selected policy detail ──
  const [detail, setDetail] = useState(null); // full proposal detail object
  const [showViewModal, setShowViewModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [extendingOID, setExtendingOID] = useState(null); // Track which row's extend button is processing

  // ── Extension state ──
  const [coverPeriod, setCoverPeriod] = useState('');
  const [commencementDate, setCommencementDate] = useState('');
  const [commencementUnix, setCommencementUnix] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryUnix, setExpiryUnix] = useState(null);
  const [coverDays, setCoverDays] = useState('');
  const [numberOfDays] = useState(getDaysInCurrentMonth());
  const [today] = useState(getNow());

  // ── Double insurance ──
  const [doubleStatus, setDoubleStatus] = useState(null); // null, true, false
  const [doubleMessage, setDoubleMessage] = useState('');
  const [doubleChecking, setDoubleChecking] = useState(false);

  // ── Premium calc ──
  const [isCalculated, setIsCalculated] = useState(false);
  const [premium, setPremium] = useState(0);
  const [basicPremium, setBasicPremium] = useState(0);
  const [payAmount, setPayAmount] = useState(0);
  const [commission, setCommission] = useState(0);
  const [netCommission, setNetCommission] = useState(0);
  const [netPremium, setNetPremium] = useState(0);
  const [PHCF, setPHCF] = useState(0);
  const [trainingLevy, setTrainingLevy] = useState(0);
  const [whtax, setWhtax] = useState(0);
  const [commissionRate, setCommissionRate] = useState('');

  // ── Payment ──
  const [mobile, setMobile] = useState('');
  const [mpesaCode, setMpesaCode] = useState('');
  const [checkMpesaReady, setCheckMpesaReady] = useState(false);

  // ── Extension flow state ──
  const [ext, setExt] = useState(null);
  const [insuredSchedule, setInsuredSchedule] = useState('');
  const [cmm, setCmm] = useState(null);
  const [extendText, setExtendText] = useState('EXTEND POLICY');
  const [promptID, setPromptID] = useState(0);
  const [verifyText, setVerifyText] = useState('VERIFY');
  const [retryText, setRetryText] = useState('Retry');

  const [paymentMode, setPaymentMode] = useState(0); // 'wallet' or 'mpesa'



  // ── Certificate ──
  const [certStatus, setCertStatus] = useState(0); // 0=pending, 1=success, 2=error
  const [certURL, setCertURL] = useState('');
  const [certError, setCertError] = useState('');

  // ══════════════════════════════════════
  //  DERIVED STATE
  // ══════════════════════════════════════
  const filteredPolicies = useMemo(() => {
    if (!startDate && !endDate) return policies;
    return policies.filter((p) => {
      const doneMs = dateToMs(p.DateDone);
      if (startDate && endDate) return doneMs > startDate && doneMs < endDate;
      return true; // only startDate set = show all
    });
  }, [policies, startDate, endDate]);

  const totalPages = Math.ceil(filteredPolicies.length / ROWS_PER_PAGE);
  const indexOfLast = currentPage * ROWS_PER_PAGE;
  const indexOfFirst = indexOfLast - ROWS_PER_PAGE;
  const currentPolicies = filteredPolicies.slice(indexOfFirst, indexOfLast);

  // ══════════════════════════════════════
  //  API HELPER
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Authorization': `Basic ${credentials}`,
    'Content-Type': 'application/json',
  }), [credentials]);

  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      method: options.method || 'GET',
      headers: authHeaders(),
      ...options,
    });
    return res.json();
  }, [authHeaders]);

  const insertLog = useCallback(async (agentId, activity) => {
    try { await apiFetch(`${API}/insertLogs?activity=${encodeURIComponent(activity)}&agent=${agentId}`); } catch {}
  }, [apiFetch]);

  // ══════════════════════════════════════
  //  INIT — credentials, agent, data
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsAuth(true);
      } catch (err) { toast.error('Failed to load credentials'); }
    })();
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details + IRA number
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const [dealer, ira] = await Promise.all([
          apiFetch(`${API}/getDealerdetails?email=${agent}`),
          apiFetch(`${API}/getIranumber?agent=${agent}`),
        ]);
        setAgentCode(dealer.OID);
        setAgentName(dealer.Dealer_name);
        setDealerCode(dealer.Dealer_type);
        setDsfApproved(dealer.DSFAllowed);
        setIsActive(!!dealer.Approved);
        setIraNumber(ira.IRANumber || '');
      } catch (err) { toast.error('Failed to load agent details'); }
    })();
  }, [agent, isAuth, apiFetch]);

  // Policies, wallet, commission
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    (async () => {
      try {
        setLoading(true);
        const [polData, walletData, commData] = await Promise.all([
          apiFetch(`${API}/Activepolicies?agentCode=${agentCode}`),
          apiFetch(`${API}/getWallet?agent=${agentCode}`),
          apiFetch(`${API}/getUtilizedCommission?agent=${agentCode}`),
        ]);
        setPolicies(Array.isArray(polData) ? polData : []);
        setWalletNo(walletData.WalletNo || '');
        setAvailableCommission(commData.AvailableCommission ?? 0);
      } catch (err) { toast.error('Failed to load policies'); }
      finally { setLoading(false); }
    })();
  }, [agentCode, isAuth, apiFetch]);

  // Wallet balance
  useEffect(() => {
    if (!walletNo || !isAuth) return;
    (async () => {
      try {
        const data = await apiFetch(`${API}/getWalletbalance?wallet=${walletNo}`);
        setWalletBalance(data.Balance || 0);
      } catch {}
    })();
  }, [walletNo, isAuth, apiFetch]);

  // Search
  useEffect(() => {
    if (!search || !agentCode || !isAuth) return;
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetch(`${API}/searchPolicy?agent=${agentCode}&search=${encodeURIComponent(search)}`);
        setPolicies(Array.isArray(data) ? data : []);
        setCurrentPage(1);
      } catch {}
    }, 400);
    return () => clearTimeout(timer);
  }, [search, agentCode, isAuth, apiFetch]);

  // ══════════════════════════════════════
  //  FETCH PROPOSAL DETAIL (shared)
  // ══════════════════════════════════════
  const fetchProposal = useCallback(async (oid) => {
    const data = await apiFetch(`${API}/getThisProposal?proposal=${oid}`);
    const option = getCoverOption(data.coption);
    return {
      oid,
      makeName: data.Description,
      modelName: data.ModelName,
      capacity: data.Capacity,
      chassis: data.ChasisNumber,
      engine: data.EngineNo,
      yom: data.YearOfManufacture,
      sumInsured: data.SumInsured,
      cc: data.CC,
      clientPhone: data.Mobile,
      tonnage: data.Tonnage,
      insuredItem: data.ItemName,
      productCode: data.Product,
      productName: data.ProductDescription,
      client: data.Dealer_name,
      proposalNo: data.ProposalNo,
      commence: data.CommencementDate,
      expiry: data.ExpiryDate,
      dateDone: data.DateDone,
      coverPeriod: data.PeriodOfCover,
      reg: data.Narration,
      grossPremium: data.GrossPremium,
      grossCommission: data.GrossCommission,
      policyNumber: data.PolicyNo,
      clientCode: data.Client,
      commissionRate: data.CommissionRate,
      PHCF: data.PHCF,
      trainingLevy: data.TrainingLevy,
      insuredOID: data.insuredOID,
      bodyType: data.BodyType,
      clientEmail: data.Email,
      clientPin: data.Pin,
      iraCode: data.IRACode,
      insuredItemCode: data.ProductInsuredItem,
      bookedBy: data.BookedBy,
      optionCode: option.code,
      optionName: option.name,
    };
  }, [apiFetch]);

  // ── View policy details ──
  const handleView = useCallback(async (oid) => {
    try {
      const d = await fetchProposal(oid);
      setDetail(d);
      setShowViewModal(true);
    } catch { toast.error('Failed to load policy details'); }
  }, [fetchProposal]);

  // ── Open extend modal ──
  const handleExtend = useCallback(async (oid) => {
    if (!isActive) return toast.error('Your account is not approved');
    if (dealerCode === 170 && dsfApproved !== '1') return toast.error('You need an agent account to proceed');
    try {
      setExtendingOID(oid);
      const d = await fetchProposal(oid);
      setDetail(d);
      resetExtensionState();
      setShowExtendModal(true);
      setExtendingOID(null);
    } catch { 
      toast.error('Failed to load policy details');
      setExtendingOID(null);
    }
  }, [isActive, dealerCode, dsfApproved, fetchProposal]);

  // ── Toggle from view → extend ──
  const toggleToExtend = useCallback(() => {
    if (!isActive) return toast.error('Your account is not approved!');
    if (![185, 187].includes(Number(dealerCode))) return toast.error('You need an agents account to proceed!');
    resetExtensionState();
    setShowViewModal(false);
    setShowExtendModal(true);
  }, [isActive, dealerCode]);

  const resetExtensionState = () => {
    setCoverPeriod('');
    setCommencementDate('');
    setCommencementUnix(null);
    setExpiryDate('');
    setExpiryUnix(null);
    setCoverDays('');
    setDoubleStatus(null);
    setDoubleMessage('');
    setDoubleChecking(false);
    setIsCalculated(false);
    setPremium(0);
    setBasicPremium(0);
    setPayAmount(0);
    setCommission(0);
    setNetCommission(0);
    setNetPremium(0);
    setPHCF(0);
    setTrainingLevy(0);
    setWhtax(0);
    setMobile('');
    setMpesaCode('');
    setExt(null);
    setInsuredSchedule('');
    setCmm(null);
    setExtendText('EXTEND POLICY');
    setPromptID(0);
    setVerifyText('VERIFY');
    setRetryText('Retry');
    setCertStatus(0);
    setCertURL('');
    setCertError('');
    setCheckMpesaReady(false);
  };

  // ══════════════════════════════════════
  //  EXTENSION — COMMENCEMENT DATE
  // ══════════════════════════════════════
  const handleCommencementChange = useCallback((e) => {
    const dateValue = e.target.value;
    const now = new Date();
    const [y, m, d] = dateValue.split('-');
    const dateWithTime = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    const ms = dateWithTime.getTime();

    setCommencementDate(formatDateFromMs(ms));
    setCommencementUnix(formatDateTime(ms));

    let monthDuration;
    if (numberOfDays === 30) monthDuration = 2592000000 + 86400000;
    else if (numberOfDays === 31) monthDuration = 2592000000;
    else monthDuration = 2592000000 + 86400000; // 28 or 29

    const weekDuration = 604800000 - 86400000;
    const fortnightDuration = 1209600000 - 86400000;
    const annualDuration = (31536000000 - (86400000)) - monthDuration;

    const periodMap = {
      Monthly: { offset: monthDuration, days: 30 },
      '3Installments': { offset: monthDuration, days: 30 },
      '6Installments': { offset: monthDuration, days: 30 },
      '10Installments': { offset: monthDuration, days: 30 },
      Week: { offset: weekDuration, days: 7 },
      Fortnight: { offset: fortnightDuration, days: 14 },
      Annually: { offset: annualDuration, days: 333 },
      '2installments': { offset: annualDuration, days: 333 },
    };

    const period = periodMap[coverPeriod];
    if (period) {
      // Special logic for 2installments: use original policy's expiry month-day
      if (coverPeriod === '2installments' && detail && detail.expiry) {
        const [origY, origM, origD] = detail.expiry.split('-');
        let expiryYear = parseInt(y);
        let expDateMs = new Date(expiryYear, parseInt(origM) - 1, parseInt(origD), now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
        
        // If calculated expiry is before or on commencement, use next year
        if (expDateMs <= ms) {
          expiryYear += 1;
          expDateMs = new Date(expiryYear, parseInt(origM) - 1, parseInt(origD), now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
        }
        
        setExpiryDate(formatDateFromMs(expDateMs));
        setExpiryUnix(formatDateTime(expDateMs));
      } else {
        // Standard logic for other payment intervals
        let exp = ms + period.offset - 86400000;
        
        // For Monthly: if the commencement month has 31 days, add 1 more day to expiry
        if (coverPeriod === 'Monthly') {
          const commencementMonth = parseInt(m);
          const monthsWith31Days = [1, 3, 5, 7, 8, 10, 12];
          if (monthsWith31Days.includes(commencementMonth)) {
            exp += 86400000; // Add 1 day
          }
        }
        
        setExpiryDate(formatDateFromMs(exp));
        setExpiryUnix(formatDateTime(exp));
      }
      setCoverDays(period.days);
    }
  }, [coverPeriod, numberOfDays, detail]);

  // ── Double insurance check ──
  useEffect(() => {
    if (!commencementDate || !detail) return;
    let cancelled = false;
    (async () => {
      try {
        setDoubleChecking(true);
        setDoubleStatus(null);
        const res = await axios.post(DOUBLE_INS_WEBHOOK, {
          chasis: detail.chassis, reg: detail.reg, commence: commencementDate, end: expiryDate,
        });
        if (cancelled) return;
        if (res.data.success) {
          setDoubleStatus(true);
          setDoubleMessage(`Vehicle has active certificate in ${res.data.company}. Expected expiry: ${res.data.endDate}`);
        } else {
          setDoubleStatus(false);
          setDoubleMessage('No double insurance');
        }
      } catch { if (!cancelled) setDoubleStatus(false); }
      finally { if (!cancelled) setDoubleChecking(false); }
    })();
    return () => { cancelled = true; };
  }, [commencementDate, detail, expiryDate]);

  // ══════════════════════════════════════
  //  CALCULATE EXTENSION PREMIUM
  // ══════════════════════════════════════
  const calculatePremium = useCallback(async () => {
    if (!detail) return;
    if (detail.bookedBy !== 'E84F0AA0-EE46-41F9-9AEB-36E34D9B2D9B' && detail.productCode !== 3586) {
      return toast.error('Extension not allowed for this policies issued at the office. Contact your branch for assistance');
    }
    try {
      const data = await apiFetch(
        `${API}/calculateExtension?insuredItem=${detail.insuredItemCode}&sumInsured=${detail.sumInsured}&capacity=${detail.capacity}&coverPeriod=${coverPeriod}`
      );
      if (!data.premium) {
        toast.error('NO PREMIUM SET! CONTACT SUPPORT');
        insertLog(agentCode, `Premium calculated for product ${detail.productName} Not found`);
        return;
      }
      const p = data.premium;
      const tl = Math.round(0.002 * p);
      const ph = Math.round(0.0025 * p);
      const basic = p + tl + ph + STAMP_DUTY;
      const com = Math.round(0.1 * p);
      const netCom = com - Math.round(0.1 * com);

      setPremium(p);
      setTrainingLevy(tl);
      setPHCF(ph);
      setBasicPremium(basic);
      setPayAmount(basic);
      setCommission(com);
      setNetCommission(netCom);
      setNetPremium(basic - netCom);
      setWhtax(Math.round(com * 0.1));
      setCommissionRate(detail.commissionRate);
      setIsCalculated(true);

      insertLog(agentCode, `Extension Premium calculated for product ${detail.productName}. Amount: Kes ${basic}`);
    } catch { toast.error('Failed to calculate premium'); }
  }, [detail, coverPeriod, agentCode, apiFetch, insertLog]);

  // ══════════════════════════════════════
  //  INITIATE EXTENSION
  // ══════════════════════════════════════
  const initiateExtension = useCallback(async () => {
    const comm = Math.round(0.1 * (detail?.grossPremium || 0));
    setCmm(comm);
    const data = await apiFetch(`${API}/extendPolicy`, {
      method: 'POST',
      body: JSON.stringify({
        clientCode: detail.clientCode, agentCode, selectedProposal: detail.oid,
        today, policyNumber: detail.policyNumber, payAmount,
        extensionStartdate: commencementUnix, extesnionExpirydate: expiryUnix,
        sumInsured: detail.sumInsured, premiumAmount: basicPremium,
        PHCF, trainingLevy, commission, whtax, netCommission, netPremium, commissionRate,
      }),
    });
    const { extension, IOID } = data;
    setExt(extension);
    setInsuredSchedule(IOID);
    insertLog(agentCode, `Extension for ${detail.oid} initiated!`);
    return { extension, IOID, success: data.success };
  }, [detail, agentCode, today, payAmount, commencementUnix, expiryUnix, basicPremium, PHCF, trainingLevy, commission, whtax, netCommission, netPremium, commissionRate, apiFetch, insertLog]);

  // ── Finalize extension (step 2) ──
  const finalizeExtension = useCallback(async (extension, utilize, transaction) => {
    await apiFetch(`${API}/extendPolicy2`, {
      method: 'POST',
      body: JSON.stringify({
        productCode: detail.productCode, utilize, extension, stampduty: STAMP_DUTY,
        clientCode: detail.clientCode, agentCode, selectedProposal: detail.oid,
        today, policyNumber: detail.policyNumber, payAmount,
        extensionStartdate: commencementUnix, extesnionExpirydate: expiryUnix,
        sumInsured: detail.sumInsured, premiumAmount: detail.grossPremium,
        PHCF, trainingLevy, commission: cmm ?? commission, whtax, netCommission, netPremium, commissionRate,
        ...(transaction ? { transaction } : {}),
      }),
    });
    insertLog(agentCode, `Extension for ${detail.oid} approved!`);
  }, [detail, agentCode, today, payAmount, commencementUnix, expiryUnix, PHCF, trainingLevy, commission, cmm, whtax, netCommission, netPremium, commissionRate, apiFetch, insertLog]);

  // ── Generate certificate ──
  const generateCertificate = useCallback(async (IOID) => {
    setExtendText('GENERATING CERTIFICATE...');
    try {
      const res = await axios.post(CERT_WEBHOOK, {
        IOID, commencementUnix, commencementDate, expiryUnix, expiryDate,
        sumInsured: detail.sumInsured, capacity: detail.capacity, tonnage: 0,
      });
      if (res.data.success) {
        await insertCertificate(res.data.certURL, res.data.certNo);
        setCertURL(res.data.certURL);
        setCertStatus(1);
        insertLog(agentCode, `Certificate for ${detail.reg} generated.`);
      } else {
        setCertStatus(2);
        setCertError(res.data.errorMessage || 'Unknown error');
        insertLog(agentCode, `Certificate for ${detail.reg} failed: ${res.data.errorMessage}`);
      }
    } catch {
      setCertStatus(2);
      setCertError('Network error generating certificate');
    }
  }, [detail, commencementUnix, commencementDate, expiryUnix, expiryDate, agentCode, insertLog]);

  // ── Insert certificate records ──
  const insertCertificate = useCallback(async (certLink, certNo) => {
    try {
      await apiFetch(`${API}/insertCertificate`, {
        method: 'POST',
        body: JSON.stringify({
          proposalOID: detail?.oid, insuredOID: detail?.insuredOID,
          expiryUnix, commencementUnix, certNo, certLink, today,
        }),
      });
    } catch {}
  }, [detail, expiryUnix, commencementUnix, today, apiFetch]);

  const insertCertificateError = useCallback(async (errorMes) => {
    try {
      await apiFetch(`${API}/insertCertificateerror`, {
        method: 'POST',
        body: JSON.stringify({
          proposalOID: detail?.oid, insuredOID: detail?.insuredOID,
          expiryUnix, commencementUnix, errorMes, today,
        }),
      });
    } catch {}
  }, [detail, expiryUnix, commencementUnix, today, apiFetch]);

  // ══════════════════════════════════════
  //  EXTEND POLICY (Mpesa STK push)
  // ══════════════════════════════════════
  const extendPolicy = useCallback(async () => {
     




    
    try {
      setExtendText('PLEASE WAIT...');
       const { extension, IOID, success, noteNumber } = await initiateExtension();
      if (!success) { setExtendText('EXTEND POLICY'); return toast.error('SOMETHING WENT WRONG'); }

      if (paymentMode === 0) {
              if (!mobile) return toast.error('Please enter Mpesa number!');
              toast.success('Prompt sent to phone!');
              setPromptID(3);

              const formattedNumber2 = '254' + mobile.slice(1);
              const ref = generateUniqueId();
              const qref = `QP${ref}`;
              const uuid = uuidv4();
              const res2 = await axios.post(PAYMENT_WEBHOOK, { formattedNumber2, payAmount, reg: detail.reg, uuid, qref, dateDone: today });

              if (res2.data.success) {
                const transaction = res2.data.transactionId;
                insertLog(agentCode, `Payment on Extension for ${detail.oid} kes.${payAmount} received - ${transaction}`);
                await finalizeExtension(extension, 0, transaction);
                await generateCertificate(IOID);
              } else {
                setPromptID(1);
                toast.error('PAYMENT NOT RECEIVED');
                setExtendText('Resend Request');
              }
      }

      else if (paymentMode === 1) {

            const receipt = await fetch(`https://uat.definiteassurance.com/api/Payments/PayWithWallet`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                Amount: payAmount,
                WalletNo: `WL${agentCode}`,
                AccountNo: noteNumber,
              })
            });
  
            if (receipt.ok) { // receipt.ok is true for status 200-299
              // Success (status 200)
              toast.success("Wallet Payment successful!");
              const approve = await fetch(`${API}/approveWalletextension`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                 payAmount,extension
                }),
                credentials: 'include'
              });
               insertLog(agentCode, `Extension approved using wallet`);
                await generateCertificate(IOID);
             
             
            } else {
              // Failed (non-200 status)
              setExtendText('EXTEND POLICY');
               insertLog(agentCode, `Extension failed using wallet`);
              toast.error("Wallet payment failed. Please try again.");
            }

      }

      


    } catch (err) {
      setExtendText('EXTEND POLICY');
      toast.error('Extension failed. Please try again.');
    }
  }, [mobile, detail, payAmount, today, agentCode, initiateExtension, finalizeExtension, generateCertificate, insertLog]);

  // ── Resend STK push ──
  const resendRequest = useCallback(async () => {
    try {
      setExtendText('Please wait...');
      toast.success('Prompt sent to phone!');
      const formattedNumber2 = '254' + mobile.slice(1);
      const ref = generateUniqueId();
      const uuid = uuidv4();
      const res2 = await axios.post(PAYMENT_WEBHOOK, { formattedNumber2, payAmount, reg: detail.reg, uuid, qref: `QP${ref}`, dateDone: today });

      if (res2.data.success) {
        insertLog(agentCode, `Payment For Extension for ${detail.proposalNo} received amount ${payAmount} - ${res2.data.transactionId}`);
        await finalizeExtension(ext, 0, res2.data.transactionId);
        await generateCertificate(insuredSchedule);
      } else {
        setPromptID(1);
        toast.error('PAYMENT NOT RECEIVED');
        setExtendText('Resend Request');
      }
    } catch { toast.error('Resend failed'); setExtendText('Resend Request'); }
  }, [mobile, detail, payAmount, today, agentCode, ext, insuredSchedule, finalizeExtension, generateCertificate, insertLog]);

  // ── Open C2B Mpesa modal ──
  const openMpesaModal = useCallback(async () => {
    try {
      await initiateExtension();
      setShowMpesaModal(true);
      setTimeout(() => setCheckMpesaReady(true), 5000);
    } catch { toast.error('Failed to initiate extension'); }
  }, [initiateExtension]);

  // ── Verify C2B Mpesa ──
  const verifyMpesaCode = useCallback(async () => {
    if (!mpesaCode) return;
    try {
      setVerifyText('Verifying...');
      const data = await apiFetch(`${API}/checkMpesa?code=${mpesaCode}&amount=${payAmount}&reg=${detail.reg}`);
      if (data.success) {
        insertLog(agentCode, `Payment for extension on proposal ${detail.proposalNo} amount ${payAmount} received`);
        toast.success('Payment received');
        setShowMpesaModal(false);

        // Finalize + cert
        setExtendText('PLEASE WAIT...');
        insertLog(agentCode, `Extension payment for proposal ${detail.oid} C2B Confirmed`);
        await finalizeExtension(ext, 0);
        await apiFetch(`${API}/processMpesa?code=${mpesaCode}`);
        await generateCertificate(insuredSchedule);
      } else {
        toast.error('No transaction with the given transaction number');
      }
    } catch { toast.error('Verification failed'); }
    finally { setVerifyText('VERIFY'); }
  }, [mpesaCode, payAmount, detail, agentCode, ext, insuredSchedule, apiFetch, finalizeExtension, generateCertificate, insertLog]);

  // ── Retry certificate ──
  const retryCertificate = useCallback(async () => {
    try {
      setRetryText('Please wait...');
      const res = await axios.post(CERT_WEBHOOK, {
        IOID: insuredSchedule, commencementUnix, commencementDate, expiryUnix, expiryDate,
        sumInsured: detail.sumInsured, capacity: detail.capacity, tonnage: 0,
      });
      if (res.data.success) {
        await insertCertificate(res.data.certURL, res.data.certNo);
        setCertURL(res.data.certURL);
        setCertStatus(1);
        insertLog(agentCode, `Certificate for ${detail.reg} generated.`);
      } else {
        setCertStatus(2);
        setCertError(res.data.errorMessage || 'Unknown error');
        await insertCertificateError(res.data.errorMessage);
        insertLog(agentCode, `Certificate for ${detail.reg} failed: ${res.data.errorMessage}`);
      }
    } catch { setCertError('Network error'); }
    finally { setRetryText('Retry'); }
  }, [insuredSchedule, commencementUnix, commencementDate, expiryUnix, expiryDate, detail, agentCode, insertCertificate, insertCertificateError, insertLog]);

  // ── Close & reload ──
  const closeAndReload = () => window.location.reload(false);

  // ══════════════════════════════════════
  //  PAGINATION HELPERS
  // ══════════════════════════════════════
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="ap-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="ap-main">
        {/* ── Top row ── */}
        <div className="ap-top-row">
          <span className="ap-company">DEFINITE ASSURANCE</span>
          <span className="ap-breadcrumb">Active Policies / Definite Assurance</span>
        </div>

        {/* ══════════════════════════════
            SEARCH / FILTER CARD
           ══════════════════════════════ */}
        <div className="ap-card">
          <h3>Active Policies</h3>
          <div className="ap-filter-bar">
            <div className="ap-filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Registration / Client name"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <div className="ap-filter-group">
              <label>From</label>
              <input type="datetime-local" onChange={(e) => setStartDate(new Date(e.target.value).getTime())} />
            </div>
            <div className="ap-filter-group">
              <label>To</label>
              <input type="datetime-local" onChange={(e) => setEndDate(new Date(e.target.value).getTime())} />
            </div>
          </div>

          {/* ── Table ── */}
          {loading ? (
            <div className="ap-loading">
              <div className="ap-spinner" />
              <p>Loading policies...</p>
            </div>
          ) : filteredPolicies.length === 0 ? (
            <div className="ap-empty">No policies found.</div>
          ) : (
            <>
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Policy No.</th>
                      <th>Date Done</th>
                      <th>Client</th>
                      <th>Product</th>
                      <th>Registration</th>
                      <th>Commencement</th>
                      <th>Expiry</th>
                      <th>Premium (KES)</th>
                      <th>Certificate</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPolicies.map((p, i) => (
                      <tr key={p.OID}>
                        <td>{indexOfFirst + i + 1}</td>
                        <td>
                          <button className="ap-link-btn" onClick={() => handleView(p.OID)}>
                            {p.PolicyNo}
                          </button>
                        </td>
                        <td>{formatISODate(p.DateDone)}</td>
                        <td>{p.Dealer_name}</td>
                        <td>{p.ProductDescription}</td>
                        <td>{p.Narration}</td>
                        <td>{formatISODate(p.CommencementDate)}</td>
                        <td>{formatISODate(p.ExpiryDate)}</td>
                        <td>{formatter(p.GrossPremium)}</td>
                        <td>
                          {p.Succeeded === 1 ? (
                            <a className="ap-cert-link" href={p.CertificatePdf} target="_blank" rel="noopener noreferrer">
                              <FaDownload style={{ marginRight: 4 }} /> Download
                            </a>
                          ) : p.ErrorMessage ? (
                            <span className="ap-no-cert" title={p.ErrorMessage}>Error</span>
                          ) : (
                            <span className="ap-no-cert">--</span>
                          )}
                        </td>
                        <td>
                          <button className="ap-extend-btn" onClick={() => handleExtend(p.OID)} disabled={extendingOID === p.OID}>
                            {extendingOID === p.OID ? (
                              <><span className="ap-spinner" style={{ marginRight: 4, display: 'inline-block' }} /> Processing</>
                            ) : (
                              <><FaExpandArrowsAlt style={{ marginRight: 4 }} /> Extend</>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ── */}
              <div className="ap-pagination">
                <span className="ap-pagination-info">
                  Showing {indexOfFirst + 1}–{Math.min(indexOfLast, filteredPolicies.length)} of {filteredPolicies.length}
                </span>
                <div className="ap-pagination-controls">
                  <button className="ap-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Prev</button>
                  {pageNumbers.map((pg) => (
                    <button key={pg} className={`ap-page-btn ${currentPage === pg ? 'active' : ''}`} onClick={() => setCurrentPage(pg)}>{pg}</button>
                  ))}
                  <button className="ap-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ══════════════════════════════════════
          VIEW MODAL
         ══════════════════════════════════════ */}
      {showViewModal && detail && (
        <div className="ap-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="ap-modal ap-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3>{detail.proposalNo}</h3>
              <button className="ap-modal-close" onClick={() => setShowViewModal(false)}><FaTimes /></button>
            </div>
            <div className="ap-modal-body">
              <div className="ap-detail-columns">
                {/* Risk details */}
                <div className="ap-detail-section">
                  <h5>Risk Details</h5>
                  <table className="ap-detail-table">
                    <tbody>
                      <tr><td>Client</td><td>{detail.client}</td></tr>
                      <tr><td>Product</td><td>{detail.productName}</td></tr>
                      <tr><td>Policy Number</td><td>{detail.policyNumber}</td></tr>
                      <tr><td>Cover Option</td><td>{detail.optionName}</td></tr>
                      <tr><td>Period of Cover</td><td>{detail.coverPeriod}</td></tr>
                      <tr><td>Commencement</td><td>{formatISODate(detail.commence)}</td></tr>
                      <tr><td>Expiry</td><td>{formatISODate(detail.expiry)}</td></tr>
                    </tbody>
                  </table>
                </div>
                {/* Insured item */}
                <div className="ap-detail-section">
                  <h5>Insured Item</h5>
                  <table className="ap-detail-table">
                    <tbody>
                      <tr><td>Item</td><td>{detail.insuredItem}</td></tr>
                      <tr><td>Premium</td><td>KES {formatter(detail.grossPremium)}</td></tr>
                      <tr><td>Registration</td><td>{detail.reg}</td></tr>
                      <tr><td>Make</td><td>{detail.makeName}</td></tr>
                      <tr><td>Model</td><td>{detail.modelName}</td></tr>
                      <tr><td>YOM</td><td>{detail.yom}</td></tr>
                      <tr><td>Tonnage</td><td>{detail.tonnage}</td></tr>
                      <tr><td>Chassis</td><td>{detail.chassis}</td></tr>
                      <tr><td>Engine</td><td>{detail.engine}</td></tr>
                      <tr><td>Capacity</td><td>{detail.capacity}</td></tr>
                      <tr><td>Sum Insured</td><td>KES {formatter(detail.sumInsured)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="ap-modal-actions">
                <button className="ap-btn ap-btn-primary" onClick={toggleToExtend}>Extend Policy</button>
                <button className="ap-btn ap-btn-outline" onClick={() => setShowViewModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          EXTEND MODAL
         ══════════════════════════════════════ */}
      {showExtendModal && detail && (
        <div className="ap-modal-overlay">
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3>Extend — {detail.proposalNo}</h3>
              <button className="ap-modal-close" onClick={certStatus > 0 ? closeAndReload : () => setShowExtendModal(false)}><FaTimes /></button>
            </div>
            <div className="ap-modal-body">

              {/* ── Certificate result ── */}
              {certStatus === 1 && (
                <div className="ap-cert-success">
                  <a href={certURL} target="_blank" rel="noreferrer">Download Certificate</a>
                  <p style={{ marginTop: 10, color: '#888' }}>Extension successful!</p>
                  <button className="ap-btn ap-btn-outline" style={{ marginTop: 12 }} onClick={closeAndReload}>Close</button>
                </div>
              )}

              {certStatus === 2 && (
                <div className="ap-cert-error">
                  <p>Certificate Error: {certError}</p>
                  <button className="ap-btn ap-btn-success" onClick={retryCertificate}><FaRedo style={{ marginRight: 4 }} />{retryText}</button>
                  <button className="ap-btn ap-btn-outline" style={{ marginLeft: 8 }} onClick={closeAndReload}>Close</button>
                </div>
              )}

              {/* ── Extension form ── */}
              {certStatus === 0 && (
                <>
                  {/* Cover period */}
                  <div className="ap-form-group">
                    <label>Payment Interval</label>
                    <select value={coverPeriod} onChange={(e) => setCoverPeriod(e.target.value)}>
                      <option value="" disabled>Select interval</option>
                      {PSV_PRODUCTS.includes(Number(detail.productCode)) ? (
                        <>
                          <option value="Week">Weekly</option>
                          <option value="Fortnight">Fortnight</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Annually">Annually</option>
                          <option value="3Installments">Three Installments</option>
                          <option value="6Installments">Six Installments</option>
                          <option value="10Installments">Ten Installments</option>
                        </>
                      ) : (
                        <>
                          {/* <option value="Annually">Annually</option> */}
                          <option value="2installments">Second Installment</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="ap-form-row">
                    <div className="ap-form-group">
                      <label>Commencement Date</label>
                      <input type="date" min={getMinDateString()} onChange={handleCommencementChange} />
                    </div>
                    <div className="ap-form-group">
                      <label>Expiry Date</label>
                      <input type="text" value={expiryUnix ? formatDate2(expiryUnix) : '--'} disabled />
                    </div>
                  </div>

                  {/* Double insurance status */}
                  {doubleChecking && (
                    <div className="ap-double-status loading">Checking double insurance...</div>
                  )}
                  {!doubleChecking && doubleStatus === false && (
                    <div className="ap-double-status success">✓ No double insurance</div>
                  )}
                  {!doubleChecking && doubleStatus === true && (
                    <div className="ap-double-status danger">{doubleMessage}</div>
                  )}

                  {/* Calculate button */}
                  {!doubleStatus && commencementDate && !isCalculated && (
                    <button className="ap-btn ap-btn-success" style={{ marginBottom: 16 }} onClick={calculatePremium}>
                      Calculate Premium
                    </button>
                  )}

                  {/* Premium summary + payment */}
                  {isCalculated && (
                    <>
                      <div className="ap-premium-summary">
                        <h4>Premium Breakdown</h4>
                        <div className="ap-premium-row"><span>Premium</span><span>KES {formatter(premium)}</span></div>
                        <div className="ap-premium-row"><span>Training Levy</span><span>KES {formatter(trainingLevy)}</span></div>
                        <div className="ap-premium-row"><span>PHCF</span><span>KES {formatter(PHCF)}</span></div>
                        <div className="ap-premium-row"><span>Stamp Duty</span><span>KES {formatter(STAMP_DUTY)}</span></div>
                        <div className="ap-premium-row"><span>Total Payable</span><span>KES {formatter(payAmount)}</span></div>
                      </div>
                       <div className="gc-form-group" style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Select Payment Method:</label>
                      <div style={{ display: 'flex', gap: 24 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={paymentMode === 0}
                            onChange={() => {
                           
                              setPaymentMode(0);
                            }}
                          />
                          Lipa na Mpesa
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={paymentMode === 1}
                            onChange={() => {
                            
                              setPaymentMode(1);
                            }}
                          />
                          Pay from Wallet
                        </label>
                      </div>
                    </div>

                      {paymentMode === 0 && (
                        <>
                          <div className="ap-form-group">
                          <label>M-Pesa Number</label>
                          <input type="text" placeholder="e.g. 0712345678" value={mobile} onChange={(e) => setMobile(e.target.value)} />
                        </div>

                        {(promptID === 0 || promptID === 3) && (
                          <button className="ap-btn ap-btn-primary" onClick={extendPolicy} disabled={extendText !== 'EXTEND POLICY'}>
                            {extendText}
                          </button>
                        )}
                        {promptID === 1 && (
                          <button className="ap-btn ap-btn-success" onClick={resendRequest} disabled={extendText === 'Please wait...'}>
                            {extendText}
                          </button>
                        )}
                        
                        </>
                      )
                      }


                       {paymentMode === 1 && 

                      <>
                        {walletBalance < payAmount && <p style={{ color: '#c0392b', marginBottom: 12,fontSize:14 }}>Insufficient wallet balance. Please top up to proceed.</p>}
                        {walletBalance >= payAmount && 
                          <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={extendPolicy} >
                            {extendText}
                          </button>
                      }
                      
                      </>
                    }


                    
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          MPESA C2B VERIFY MODAL
         ══════════════════════════════════════ */}
      {showMpesaModal && (
        <div className="ap-modal-overlay">
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3>Confirm Payment</h3>
              <button className="ap-modal-close" onClick={() => { setCheckMpesaReady(false); setShowMpesaModal(false); }}><FaTimes /></button>
            </div>
            <div className="ap-modal-body">
              {!checkMpesaReady ? (
                <div className="ap-loading">
                  <div className="ap-spinner" />
                  <p>Checking payment, please wait...</p>
                </div>
              ) : (
                <>
                  <p style={{ color: '#c0392b', fontWeight: 600, marginBottom: 12 }}>Enter the M-Pesa transaction code below</p>
                  <div className="ap-form-group">
                    <label>Transaction Code</label>
                    <input type="text" placeholder="e.g. SJ12ABCDEF" value={mpesaCode} onChange={(e) => setMpesaCode(e.target.value.toUpperCase())} />
                  </div>
                  <button className="ap-btn ap-btn-success" onClick={verifyMpesaCode} disabled={verifyText !== 'VERIFY'}>
                    {verifyText}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
